// ToolsMenu.jsx: the dashboard's single "Tools" menu for the communication tools.
// Keyboard: Enter/Space or ArrowDown opens and focuses the first tool, arrows move,
// Escape closes and returns focus to the button. Clicking outside closes it.
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, MessageSquare, Users, Heart, Vote, Wrench } from 'lucide-react';

const TOOLS = [
  { href: '/free/siyw', icon: MessageSquare, name: 'Say It Your Way', hint: 'Turn what you feel into a clear statement' },
  { href: '/free/together', icon: Users, name: 'Say It Together', hint: 'Talk it through with the other person' },
  { href: '/free/mood', icon: Heart, name: 'Mood Journal', hint: 'Check in and spot patterns' },
  { href: '/free/vote', icon: Vote, name: 'Vote Mode', hint: 'Decide fairly as a group' },
];

function ToolsMenu({ voteHasNew = false }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);
  const button = useRef(null);
  const items = useRef([]);

  useEffect(() => {
    if (!open) return undefined;
    items.current[0]?.focus();
    const onClick = e => { if (!wrapper.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onMenuKey = (e) => {
    const i = items.current.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); button.current?.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); items.current[(i + 1) % TOOLS.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); items.current[(i - 1 + TOOLS.length) % TOOLS.length]?.focus(); }
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <div className="relative" ref={wrapper}>
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}
        className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-50 border border-stone-300 text-stone-800 hover:bg-stone-100 transition-colors text-xs font-medium whitespace-nowrap"
      >
        {voteHasNew && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 animate-pulse" aria-hidden="true" />}
        <Wrench className="w-3.5 h-3.5" aria-hidden="true" />
        Tools{voteHasNew && <span className="sr-only"> (new Vote Mode activity)</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul role="menu" aria-label="Tools" onKeyDown={onMenuKey}
          className="absolute left-0 top-full mt-2 z-30 w-72 bg-white border border-stone-200 rounded-xl shadow-lg p-1.5">
          {TOOLS.map((tool, i) => (
            <li key={tool.href} role="none">
              <a role="menuitem" href={tool.href} ref={el => { items.current[i] = el; }}
                className="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-stone-100 focus:bg-stone-100 focus:outline-none">
                <tool.icon className="w-4 h-4 mt-0.5 text-stone-700" aria-hidden="true" />
                <span>
                  <span className="block text-sm font-medium text-stone-800">
                    {tool.name}
                    {tool.href === '/free/vote' && voteHasNew && <span className="ml-2 text-xs text-amber-800">New activity</span>}
                  </span>
                  <span className="block text-xs text-stone-600">{tool.hint}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ToolsMenu;
